import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VATReturnCard = ({ period, status, dueDate, netLiability, inputVAT, outputVAT, onViewDetails, onFile, completionPercentage, missingItems }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-warning/10 text-warning border-warning/20';
      case 'ready': return 'bg-success/10 text-success border-success/20';
      case 'filed': return 'bg-primary/10 text-primary border-primary/20';
      case 'overdue': return 'bg-error/10 text-error border-error/20';
      default: return 'bg-muted/10 text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'draft': return 'Edit';
      case 'ready': return 'CheckCircle';
      case 'filed': return 'FileCheck';
      case 'overdue': return 'AlertTriangle';
      default: return 'Clock';
    }
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilDue = getDaysUntilDue(dueDate);

  const handleViewDetails = async () => {
    try {
      setIsProcessing(true);
      if (onViewDetails && typeof onViewDetails === 'function') {
        await onViewDetails();
      } else {
        console.warn('VATReturnCard: onViewDetails handler not provided or not a function');
      }
    } catch (error) {
      console.error('Error viewing VAT return details:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileReturn = async () => {
    try {
      setIsProcessing(true);
      if (onFile && typeof onFile === 'function') {
        await onFile();
      }
    } catch (error) {
      console.error('Error filing VAT return:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getUrgencyIndicator = () => {
    if (status === 'overdue') {
      return (
        <div className="bg-error/10 border border-error/20 rounded-lg p-2 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="AlertTriangle" size={14} className="text-error" />
            <span className="text-sm font-medium text-error">
              {Math.abs(daysUntilDue)} days overdue - Immediate action required
            </span>
          </div>
        </div>
      );
    }
    
    if (daysUntilDue <= 7 && daysUntilDue > 0 && status !== 'filed') {
      return (
        <div className="bg-warning/10 border border-warning/20 rounded-lg p-2 mb-4">
          <div className="flex items-center space-x-2">
            <Icon name="Clock" size={14} className="text-warning" />
            <span className="text-sm font-medium text-warning">
              Due in {daysUntilDue} days - Action needed soon
            </span>
          </div>
        </div>
      );
    }
    
    return null;
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 hover:shadow-elevated transition-all duration-200 hover:border-primary/30">
      {getUrgencyIndicator()}
      
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-2">{period}</h3>
          <div className="flex items-center space-x-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(status)}`}>
              <Icon name={getStatusIcon(status)} size={12} className="mr-1.5" />
              {status?.charAt(0)?.toUpperCase() + status?.slice(1)}
            </span>
          </div>
        </div>
        <div className="text-right">
          <p className="text-sm text-muted-foreground mb-1">Net Liability</p>
          <p className="text-2xl font-bold text-foreground">${netLiability?.toLocaleString()}</p>
        </div>
      </div>

      {/* VAT Breakdown */}
      {(inputVAT !== undefined && outputVAT !== undefined) && (
        <div className="bg-muted/10 rounded-lg p-3 mb-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Input VAT</p>
              <p className="font-medium text-foreground">${inputVAT?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Output VAT</p>
              <p className="font-medium text-foreground">${outputVAT?.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <p className="text-sm text-muted-foreground mb-1">Due Date</p>
          <p className="font-medium text-foreground">{new Date(dueDate)?.toLocaleDateString()}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground mb-1">Status</p>
          <p className="font-medium text-foreground capitalize">{status}</p>
        </div>
      </div>

      <div className="flex space-x-2">
        <Button
          variant="outline"
          size="sm"
          onClick={handleViewDetails}
          iconName="Eye"
          iconPosition="left"
          className="flex-1"
          loading={isProcessing}
          disabled={isProcessing}
        >
          View Details
        </Button>
        {status !== 'filed' && (
          <Button
            variant={status === 'ready' ? 'default' : status === 'overdue' ? 'danger' : 'secondary'}
            size="sm"
            onClick={handleFileReturn}
            iconName={status === 'overdue' ? 'AlertTriangle' : 'Send'}
            iconPosition="left"
            className="flex-1"
            loading={isProcessing}
            disabled={isProcessing}
          >
            {status === 'ready' ? 'File Return' : 
             status === 'overdue' ? 'File Now!' : 'Prepare'}
          </Button>
        )}
        {status === 'filed' && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleViewDetails}
            iconName="Download"
            iconPosition="left"
            className="flex-1"
          >
            Download
          </Button>
        )}
      </div>

      {/* Progress indicator for draft returns - now using props with proper fallbacks */}
      {status === 'draft' && completionPercentage !== undefined && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Completion</span>
            <span className="text-sm font-medium text-foreground">
              {completionPercentage ? `${Math.round(completionPercentage)}%` : 'No data'}
            </span>
          </div>
          <div className="w-full bg-muted/30 rounded-full h-2">
            <div 
              className="bg-primary h-2 rounded-full transition-all duration-300" 
              style={{ width: completionPercentage ? `${Math.round(completionPercentage)}%` : '0%' }}
            ></div>
          </div>
          {missingItems && missingItems?.length > 0 ? (
            <p className="text-xs text-muted-foreground mt-2">
              Missing: {missingItems?.join(', ')}
            </p>
          ) : completionPercentage < 100 ? (
            <p className="text-xs text-muted-foreground mt-2">
              Incomplete data - check transaction entries
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default VATReturnCard;